/**
 * debug.ts — debug affordances (AGENTS.md).
 *
 * URL params:  ?lag=200   simulated round-trip latency in ms
 *              ?predict=0 turn client-side prediction off
 *              ?volume=0.2 starting master volume, 0..1
 *              ?json=1    reserved: force the JSON codec once binary lands (M6)
 * Keybinds:    K          collider wireframes
 *              J          cycle the baked sword-trajectory preview
 *              M          mute
 *              N          audition the next combat sound
 *              F3         debug readout
 */

export interface DebugOptions {
  /** Simulated round-trip latency, ms. Split evenly between the two directions. */
  lagMs: number;
  /**
   * Whether the local player is predicted. On by default; `?predict=0` puts the
   * client back on pure interpolation, which is the only honest way to judge what
   * prediction is buying — pair it with `?lag=` and the difference is the point.
   */
  predict: boolean;
  /**
   * Starting master volume, or undefined to use the default. There is no
   * persistence for it (no browser storage in the client), so a URL param is how
   * you start a session somewhere other than the default.
   */
  volume: number | undefined;
}

export function readDebugOptions(): DebugOptions {
  const params = new URLSearchParams(location.search);
  const lag = Number(params.get('lag') ?? 0);
  const rawVolume = params.get('volume');
  const volume = rawVolume === null ? Number.NaN : Number(rawVolume);
  return {
    lagMs: Number.isFinite(lag) && lag > 0 ? Math.min(lag, 2000) : 0,
    predict: params.get('predict') !== '0',
    volume: Number.isFinite(volume) ? Math.min(1, Math.max(0, volume)) : undefined,
  };
}

/**
 * Whether a key event is going into a text field, and so is somebody typing
 * rather than pressing a game key.
 *
 * Lives here beside the key helpers because EVERY global keybind needs it, not
 * just chat's: without it, typing "kim" into the chat box toggles the collider
 * wireframes, mutes the sound and opens the armory. Any new `window` keydown
 * listener has to consult this — the two helpers below do it for you.
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Registers a keydown handler for a single key. Returns a disposer. */
export function onKeyPress(code: string, press: () => void): () => void {
  const handler = (ev: KeyboardEvent): void => {
    if (ev.code !== code || ev.repeat || isTextEntryTarget(ev.target)) return;
    press();
  };
  window.addEventListener('keydown', handler);
  return () => window.removeEventListener('keydown', handler);
}

/** Registers a keydown toggle. Returns a disposer. */
export function onKeyToggle(code: string, toggle: (on: boolean) => void): () => void {
  let on = false;
  const handler = (ev: KeyboardEvent): void => {
    if (ev.code !== code || ev.repeat || isTextEntryTarget(ev.target)) return;
    on = !on;
    toggle(on);
  };
  window.addEventListener('keydown', handler);
  return () => window.removeEventListener('keydown', handler);
}
